import { useEffect, useState } from "react";
import { Activity } from "lucide-react";

import api from "@/api";
import { useI18n } from "@/i18n";

const COLORS = {
  success: "bg-[#00FF87]",
  quota_exhausted: "bg-amber-400",
  skipped_quota: "bg-amber-400/60",
  interrupted: "bg-red-400",
  error: "bg-red-400",
  running: "bg-[#00F0FF]",
};

export const QuotaTimeline = () => {
  const { t } = useI18n();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/admin/quota/timeline").then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  if (!data) return null;
  const max = Math.max(1, ...data.buckets.map((b) => b.requests));

  return (
    <div className="mb-6 rounded-xl p-4 hud-card" data-testid="quota-timeline">
      <h2 className="mb-1 flex items-center gap-2 font-mono2 text-xs font-bold uppercase tracking-widest text-[#FFD600]">
        <Activity className="h-3.5 w-3.5" /> {t("admin.timeline_title")}
      </h2>
      <p className="mb-3 font-mono2 text-[9px] uppercase tracking-[.04em] text-slate-500" data-testid="quota-timeline-summary">
        {t("admin.timeline_summary", { total: data.total_24h, peak: data.peak_hour ?? "—" })}
      </p>
      <div className="flex items-end gap-[3px]" style={{ height: 96 }}>
        {data.buckets.map((b) => (
          <div key={b.hour} className="group relative flex flex-1 flex-col items-center justify-end" data-testid={`timeline-bar-${b.label}`}>
            {b.runs.length > 0 && (
              <span className={`mb-0.5 h-1.5 w-1.5 rounded-full ${COLORS[b.runs[b.runs.length - 1].status] || "bg-slate-500"}`} />
            )}
            <div
              className="w-full rounded-t bg-[#00FF87]/70 transition-colors group-hover:bg-[#00FF87]"
              style={{ height: `${Math.round((b.requests / max) * 72)}px`, minHeight: b.requests ? 2 : 1 }}
            />
            <span className="mt-1 font-mono2 text-[7.5px] text-slate-600">{b.label}</span>
            <div className="pointer-events-none absolute bottom-full left-1/2 z-20 hidden w-44 -translate-x-1/2 rounded-md border border-[#1F293D] bg-[#0B0E14] p-2 font-mono2 text-[9px] text-slate-300 shadow-xl group-hover:block">
              <p className="font-bold text-[#00FF87]">{b.label}:00 UTC · {b.requests} req</p>
              {b.runs.length === 0
                ? <p className="text-slate-500">{t("admin.timeline_no_run")}</p>
                : b.runs.map((r, i) => (
                  <p key={`${r.job}-${i}`} className="truncate">
                    {r.at} {r.job} · <span className={r.status === "success" ? "text-[#00FF87]" : "text-amber-400"}>{r.status}</span>
                    {r.requests ? ` (${r.requests})` : ""}
                  </p>
                ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
